import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { Modal } from 'components/UI/Modal'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { StageChevronBar } from 'components/SalesStage/StageChevronBar'
import apiService from 'services/api'

interface UpdateSalesStageModalProps {
  dealId: number
  dealIdentifier: string | null
  open: boolean
  onClose: () => void
}

/**
 * Modal for updating a deal's sales stage from the email thread view.
 * Shows the current stage in a chevron progress bar, an "Or select" dropdown,
 * and Cancel/Apply confirmation buttons.
 */
export function UpdateSalesStageModal({
  dealId,
  dealIdentifier,
  open,
  onClose,
}: UpdateSalesStageModalProps) {
  const api = apiService()
  const queryClient = useQueryClient()

  const { data: deal } = api.useGetDeal(dealId, open)
  const { data: stageOptions } = api.useGetSalesStageOptions()

  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // Build sorted SalesStage[] from the options
  const salesStages = useMemo(() => {
    const stages =
      stageOptions?.filter((s) => s.original).map((s) => s.original!) ?? []
    return [...stages].sort((a, b) =>
      customOrderEnabled
        ? a.order - b.order
        : (a.win_rate || 0) - (b.win_rate || 0)
    )
  }, [stageOptions, customOrderEnabled])

  const currentStageId = deal?.sales_stage ?? undefined
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null)

  // The stage to display — user selection takes precedence, then the deal's current stage
  const activeStageId = selectedStageId ?? currentStageId ?? null

  const currentStageName = useMemo(() => {
    if (!currentStageId || !stageOptions) return null
    return stageOptions.find((s) => s.value === currentStageId)?.label ?? null
  }, [currentStageId, stageOptions])

  const handleStageChange = useCallback(
    (stageId: number) => setSelectedStageId(stageId),
    []
  )

  const handleSelectChange = useCallback((option: unknown) => {
    const selected = option as { value: number } | null
    setSelectedStageId(selected?.value ?? null)
  }, [])

  const updateMutation = useMutation({
    mutationFn: (stageId: number) =>
      api.editDeal(dealId, { sales_stage: stageId }),
    onSuccess: () => {
      toast.success('Sales stage updated')
      queryClient.invalidateQueries({ queryKey: ['email-threads'] })
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      onClose()
    },
    onError: () => toast.error('Failed to update sales stage'),
  })

  const handleApply = useCallback(() => {
    if (selectedStageId) {
      updateMutation.mutate(selectedStageId)
    }
  }, [selectedStageId, updateMutation])

  const handleClose = useCallback(() => {
    setSelectedStageId(null)
    onClose()
  }, [onClose])

  // Build select value from selectedStageId
  const selectValue = useMemo(() => {
    if (!selectedStageId || !stageOptions) return null
    return stageOptions.find((s) => s.value === selectedStageId) ?? null
  }, [selectedStageId, stageOptions])

  return (
    <Modal
      show={open}
      onHide={handleClose}
      centered
      className="[&_.modal-content]:rounded-xl [&_.modal-content]:overflow-hidden [&_.modal-content]:border-0"
    >
      <div className="flex flex-col rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 pb-4">
          <div className="flex flex-col gap-1">
            {dealIdentifier && (
              <span className="text-xs font-light text-fg tracking-[0.36px] uppercase">
                {dealIdentifier}
              </span>
            )}
            <h2 className="text-xl font-semibold text-fg leading-7">
              Update Sales Stage
            </h2>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          {/* Current stage label */}
          <div className="mb-3">
            <span className="text-sm font-medium text-fg">
              Update Current Stage:{' '}
              <span className="font-bold">
                {currentStageName ?? 'No Stage'}
              </span>
            </span>
          </div>

          {/* Stage progress bar */}
          <div className="mb-4">
            <StageChevronBar
              stages={salesStages}
              activeStageId={activeStageId}
              onStageChange={handleStageChange}
            />
          </div>

          {/* Or select dropdown */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg">Or select:</span>
            <ReactSelectWrapper
              options={stageOptions ?? []}
              value={selectValue}
              onChange={handleSelectChange}
              placeholder="Select a sales stage"
              isClearable
            />
          </div>
        </div>

        {/* Footer — gray background with full-width equal buttons */}
        <div className="px-6 py-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-12 flex items-center justify-center bg-surface border border-line rounded-md text-base font-medium text-fg-secondary hover:bg-surface-alt transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!selectedStageId || updateMutation.isPending}
              className="flex-1 h-12 flex items-center justify-center bg-brand rounded-md text-base font-medium text-white hover:bg-brand-pressed transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
